import { useState, useEffect, useCallback } from "react";

export type UserLocation = {
  latitude: number;
  longitude: number;
} | null;

export type UseUserLocationResult = {
  location: UserLocation;
  permissionGranted: boolean;
  error: string | null;
  /** Re-request location permission and start watching if granted. */
  requestPermission: () => Promise<void>;
};

// Browser geolocation has no separate permission-request step: the first
// getCurrentPosition/watchPosition call triggers the native prompt. We keep the
// same two-phase shape as before (permission gate → watch) so consumers are
// unchanged: requestPermission probes with a one-shot getCurrentPosition, and
// only once granted does the watch subscription spin up.
export function useUserLocation(accuracySensors = false): UseUserLocationResult {
  const [location, setLocation] = useState<UserLocation>(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestPermission = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setError("Location is not available");
      setPermissionGranted(false);
      return;
    }
    return new Promise<void>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setError(null);
          setPermissionGranted(true);
          setLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          resolve();
        },
        (err) => {
          setError(
            err.code === err.PERMISSION_DENIED
              ? "Location permission denied"
              : "Unable to determine location",
          );
          setPermissionGranted(false);
          resolve();
        },
        { enableHighAccuracy: accuracySensors },
      );
    });
  }, [accuracySensors]);

  useEffect(() => {
    requestPermission();
  }, [requestPermission]);

  useEffect(() => {
    if (!permissionGranted) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    let cancelled = false;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (!cancelled) {
          setLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        }
      },
      () => {
        // Transient errors are ignored — a later fix delivers an update.
      },
      { enableHighAccuracy: accuracySensors, maximumAge: 5000 },
    );

    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [permissionGranted, accuracySensors]);

  return { location, permissionGranted, error, requestPermission };
}
